<template>
  <header class="grid grid-cols-12 pb-2 h-auto pl-4 pr-6 bg-blue-600 pt-[calc(env(safe-area-inset-top)+0.5rem)]">
    <div class="col-span-4 flex items-center">
      <button class="bg-transparent text-white active:scale-75 hover:scale-100 relative" @click="smallMenu">
        <svg class="w-5 h-5 mr-5">
          <use href="@/assets/svg/icon.svg#menu" />
        </svg>
      </button>
      <input type="search"
        class="py-2 px-2 rounded-lg hidden md:block outline-none bg-blue-500 text-gray-50 text-sm cursor-pointer placeholder:text-blue-50 w-full"
        placeholder="Buscar" @click="activeModalModules" readonly />
    </div>
    <div class="col-span-8 h-[100%] flex items-center justify-end">
      <div
        v-if="activeStore.name"
        class="mr-3 flex max-w-[220px] items-center gap-2 rounded-md border border-white/30 bg-white/10 px-3 py-1 text-white"
        :title="activeStoreTitle"
      >
        <font-awesome-icon icon="fa-solid fa-store" class="h-3.5 w-3.5 flex-shrink-0" />
        <div class="min-w-0 leading-tight">
          <p class="truncate text-[11px] font-bold uppercase">{{ activeStore.name }}</p>
          <p class="truncate text-[10px] text-blue-50">{{ activeStore.typeLabel }}</p>
        </div>
      </div>
      <div v-if="canViewAnyNotifications" class="relative mr-1">
        <button
          class="bg-transparent text-white active:scale-75 hover:scale-110 relative mt-[5px] notification-bell-button"
          :class="{ 'notification-bell-active': totalNotifications > 0 }" @click="toggleNotifications">
          <svg class="w-4 h-4 notification-bell-icon"
            :class="{ 'notification-bell-icon-active': totalNotifications > 0 }">
            <use href="@/assets/svg/icon.svg#bell" />
          </svg>
          <span v-if="totalNotifications > 0"
            class="w-[5px] h-[5px] bg-white absolute top-[-10px] right-[-3px] rounded-full animate-ping"></span>
          <span v-if="totalNotifications > 0"
            class="absolute -top-4 -right-3 min-w-[18px] h-[18px] px-1 rounded-full bg-white text-blue-700 text-[10px] font-bold flex items-center justify-center">
            {{ totalNotifications > 99 ? '99+' : totalNotifications }}
          </span>
        </button>

        <div v-if="showNotifications"
          class="absolute top-full right-0 mt-3 w-[360px] max-w-[calc(100vw-2rem)] bg-white rounded-xl shadow-2xl overflow-hidden z-40 border border-blue-100">
          <div class="px-4 py-3 border-b bg-blue-50 flex items-center justify-between gap-3">
            <div>
              <p class="text-sm font-bold text-blue-800">Notificaciones</p>
              <p class="text-xs text-slate-500">
                {{ totalNotifications > 0 ? `${totalNotifications} alerta(s) requieren atención` : 'Sin alertas activas'
                }}
              </p>
            </div>
            <button class="text-xs font-semibold text-blue-700 hover:text-blue-900" @click="refreshNotifications">
              Actualizar
            </button>
          </div>

          <div v-if="notificationsLoading" class="px-4 py-6 text-sm text-slate-500">
            Cargando notificaciones...
          </div>

          <div
            v-else-if="notifications.length === 0 && accountsReceivableNotifications.length === 0 && accountsPayableNotifications.length === 0 && externalPurchaseNotifications.length === 0 && branchOrderNotifications.length === 0"
            class="px-4 py-6 text-sm text-slate-500">
            No hay notificaciones activas.
          </div>

          <div v-else class="max-h-[420px] overflow-y-auto">
            <div v-if="canViewStockNotifications && notifications.length > 0" class="px-4 py-2 bg-slate-50 border-b">
              <p class="text-xs font-bold uppercase tracking-wide text-slate-500">Stock mínimo</p>
            </div>
            <button v-for="notification in notifications" :key="notification.id"
              class="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
              @click="openProductNotification(notification)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-700 uppercase truncate">
                    {{ notification.nombre_producto }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Código: {{ notification.codigo_de_barra }}
                  </p>
                </div>
                <span class="px-2 py-1 rounded-full bg-red-50 text-red-600 text-[11px] font-bold whitespace-nowrap">
                  {{ notification.stock }} / {{ notification.stock_minimo }}
                </span>
              </div>
            </button>

            <div v-if="canViewBranchOrderNotifications && branchOrderNotifications.length > 0"
              class="px-4 py-2 bg-slate-50 border-b">
              <p class="text-xs font-bold uppercase tracking-wide text-slate-500">Ordenes por preparar</p>
            </div>
            <button v-for="notification in branchOrderNotifications" :key="`branch-order-${notification.id}`"
              class="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
              @click="openBranchOrderNotification(notification)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-700 uppercase truncate">
                    {{ notification.numero_cumplimiento }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1 truncate">
                    Origen: {{ notification.sucursal_origen || '-' }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1 truncate">
                    Recibe: {{ notification.nombre_recibe || notification.cliente || '-' }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Productos: {{ notification.total_productos }} | Reservado: {{ notification.cantidad_reservada }}
                  </p>
                </div>
                <span class="px-2 py-1 rounded-full bg-emerald-50 text-emerald-600 text-[11px] font-bold whitespace-nowrap">
                  {{ notification.estado }}
                </span>
              </div>
            </button>

            <div v-if="canViewAccountsReceivableNotifications && accountsReceivableNotifications.length > 0"
              class="px-4 py-2 bg-slate-50 border-b">
              <p class="text-xs font-bold uppercase tracking-wide text-slate-500">Cuenta por cobrar</p>
            </div>
            <button v-for="notification in accountsReceivableNotifications" :key="`receivable-${notification.id}`"
              class="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
              @click="openAccountsReceivableNotification(notification)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-700 uppercase truncate">
                    Cuenta por cobrar
                  </p>
                  <p class="text-xs text-slate-500 mt-1 truncate">
                    Cliente: {{ notification.nombre_cliente_pedido }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Comprobante: {{ notification.serie_comprobante }} - {{ notification.numero_comprobante }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Saldo: {{ format(notification.saldo) }}
                  </p>
                  <p class="text-xs mt-1 font-semibold"
                    :class="notification.tipo_alerta === 'vencida' ? 'text-red-600' : 'text-orange-600'">
                    {{ notification.mensaje_alerta }}
                  </p>
                </div>
                <span class="px-2 py-1 rounded-full bg-amber-50 text-amber-600 text-[11px] font-bold whitespace-nowrap">
                  CXC
                </span>
              </div>
            </button>

            <div v-if="canViewAccountsPayableNotifications && accountsPayableNotifications.length > 0"
              class="px-4 py-2 bg-slate-50 border-b">
              <p class="text-xs font-bold uppercase tracking-wide text-slate-500">Cuenta por pagar</p>
            </div>
            <button v-for="notification in accountsPayableNotifications" :key="`payable-${notification.id}`"
              class="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
              @click="openAccountsPayableNotification(notification)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-700 uppercase truncate">
                    Cuenta por pagar
                  </p>
                  <p class="text-xs text-slate-500 mt-1 truncate">
                    Proveedor: {{ notification.razon_social }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Comprobante: {{ notification.numero_comprobante }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Saldo: {{ format(notification.saldo) }}
                  </p>
                  <p class="text-xs mt-1 font-semibold"
                    :class="notification.tipo_alerta === 'vencida' ? 'text-red-600' : 'text-orange-600'">
                    {{ notification.mensaje_alerta }}
                  </p>
                </div>
                <span class="px-2 py-1 rounded-full bg-rose-50 text-rose-600 text-[11px] font-bold whitespace-nowrap">
                  CXP
                </span>
              </div>
            </button>

            <div v-if="canViewExternalPurchaseNotifications && externalPurchaseNotifications.length > 0"
              class="px-4 py-2 bg-slate-50 border-b">
              <p class="text-xs font-bold uppercase tracking-wide text-slate-500">Compra externa DGII</p>
            </div>
            <button v-for="notification in externalPurchaseNotifications" :key="`external-${notification.id}`"
              class="w-full text-left px-4 py-3 border-b last:border-b-0 hover:bg-slate-50 transition-colors"
              @click="openExternalPurchaseNotification(notification)">
              <div class="flex items-start justify-between gap-3">
                <div class="min-w-0">
                  <p class="text-sm font-semibold text-slate-700 uppercase truncate">
                    Compra externa DGII
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    Proveedor: {{ notification.razon_social_emisor }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    e-NCF: {{ notification.ecf }}
                  </p>
                  <p class="text-xs text-slate-500 mt-1">
                    {{ notification.fecha_emision ? formatDateTime(notification.fecha_emision) :
                      formatDateTime(notification.created_at) }}
                  </p>
                </div>
                <span class="px-2 py-1 rounded-full bg-blue-50 text-blue-600 text-[11px] font-bold whitespace-nowrap">
                  DGII
                </span>
              </div>
            </button>
          </div>

          <div v-if="totalStockNotifications > notifications.length && notifications.length > 0"
            class="px-4 py-3 bg-slate-50 border-t">
            <button class="text-sm font-semibold text-blue-700 hover:text-blue-900" @click="openMinimumStockReport">
              Ver todas las alertas
            </button>
          </div>
          <div
            v-if="totalBranchOrderNotifications > branchOrderNotifications.length && branchOrderNotifications.length > 0"
            class="px-4 py-3 bg-slate-50 border-t">
            <button class="text-sm font-semibold text-blue-700 hover:text-blue-900" @click="openBranchOrders">
              Ver todas las ordenes
            </button>
          </div>
          <div
            v-if="totalAccountsReceivableNotifications > accountsReceivableNotifications.length && accountsReceivableNotifications.length > 0"
            class="px-4 py-3 bg-slate-50 border-t">
            <button class="text-sm font-semibold text-blue-700 hover:text-blue-900" @click="openAccountsReceivableList">
              Ver todas las cuentas por cobrar
            </button>
          </div>
          <div
            v-if="totalAccountsPayableNotifications > accountsPayableNotifications.length && accountsPayableNotifications.length > 0"
            class="px-4 py-3 bg-slate-50 border-t">
            <button class="text-sm font-semibold text-blue-700 hover:text-blue-900" @click="openAccountsPayableList">
              Ver todas las cuentas por pagar
            </button>
          </div>
          <div
            v-if="totalExternalPurchaseNotifications > externalPurchaseNotifications.length && externalPurchaseNotifications.length > 0"
            class="px-4 py-3 bg-slate-50 border-t">
            <button class="text-sm font-semibold text-blue-700 hover:text-blue-900" @click="openExternalPurchases">
              Ver todas las compras externas
            </button>
          </div>
        </div>
      </div>
      <div class="w-8 h-8 bg-blue-800 ml-5 rounded-full animate-pulse" v-if="loading"></div>
      <div class="relative group" v-else>
        <div class="rounded-full w-8 h-8 overflow-hidden ml-5 cursor-pointer">
          <img src="@/assets/images/defaults/avatar-default.png" alt="Foto de perfil" v-if="dataUser.avatar === null" />
          <img :src="buildApiResourceUrl(dataUser.avatar)" alt="Foto de perfil" v-else />
        </div>

        <ul
          class="absolute top-full right-0 w-[200px] bg-white rounded-md shadow-lg overflow-hidden transition-all duration-300 transform scale-0 group-hover:scale-100 z-30">
          <li class="block px-4 py-2 text-blue-700 hover:bg-gray-100 cursor-pointer capitalize">
            {{ dataUser.fullName }}
            <span class="text-[12px] mt-0 block text-gray-500">
              {{ dataUser.email }}
            </span>
          </li>
          <router-link to="/perfil" class="block px-4 py-2 text-gray-600 hover:bg-gray-100 cursor-pointer">
            Mi cuenta
          </router-link>
          <li class="block px-4 py-2 text-red-500 hover:bg-gray-100 cursor-pointer" @click="logout">
            Salir
          </li>
        </ul>
      </div>
    </div>
    <SearchMenus :hidden="activeModalModules" v-if="stateModalSearchModules" />
    <button
      v-if="liveBranchOrderAlert"
      class="fixed right-4 top-20 z-50 w-[360px] max-w-[calc(100vw-2rem)] rounded-md border border-emerald-200 bg-white p-4 text-left shadow-2xl live-branch-order-alert"
      @click="openBranchOrderNotification(liveBranchOrderAlert)"
    >
      <div class="flex items-start gap-3">
        <span class="mt-1 flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-emerald-100 text-emerald-700">
          <font-awesome-icon icon="fa-solid fa-bell" />
        </span>
        <div class="min-w-0">
          <p class="text-sm font-bold text-slate-800">Nueva orden para preparar</p>
          <p class="mt-1 truncate text-xs font-semibold text-blue-700">{{ liveBranchOrderAlert.numero_cumplimiento }}</p>
          <p class="mt-1 truncate text-xs text-slate-600">
            Origen: {{ liveBranchOrderAlert.sucursal_origen || '-' }}
          </p>
          <p class="mt-1 truncate text-xs text-slate-600">
            Recibe: {{ liveBranchOrderAlert.nombre_recibe || liveBranchOrderAlert.cliente || '-' }}
          </p>
        </div>
      </div>
    </button>
  </header>
</template>

<script>
import { computed, onMounted, onUnmounted, ref, watchEffect } from "vue";
import { useRouter } from "vue-router";
import Swal from "sweetalert2";
import SearchMenus from "@/components/Nav/SearchMenus.vue";
import axios from "@/api";
import { recoverableErrorAction } from "@/alerts";
import { idLocalStore, idLocalUser } from "@/publicjs/localStorage";
import { clearStoredSession, getStoredAccessData, hasValidSession } from "@/publicjs/authSession";
import { validateAccessSpecific } from "@/repository/Sales/ValidateAccessEspecific/ValidateAccess";
import { formatDateTime } from "@/publicjs/formatDate";
import { format } from "@/publicjs/money";
import { findSubmoduleIdByDescription } from "@/hisAcces";
import { buildApiResourceUrl } from "@/publicjs/resourceUrl";
export default {
  name: "HeaderHome",
  components: {
    SearchMenus,
  },
  setup(props, { emit }) {
    const router = useRouter();
    const active = ref(false);
    const activeParent = ref(false);
    const stateModalSearchModules = ref(false);
    const showNotifications = ref(false);
    const notifications = ref([]);
    const totalStockNotifications = ref(0);
    const accountsReceivableNotifications = ref([]);
    const totalAccountsReceivableNotifications = ref(0);
    const accountsPayableNotifications = ref([]);
    const totalAccountsPayableNotifications = ref(0);
    const externalPurchaseNotifications = ref([]);
    const totalExternalPurchaseNotifications = ref(0);
    const branchOrderNotifications = ref([]);
    const totalBranchOrderNotifications = ref(0);
    const notificationsLoading = ref(false);
    const liveBranchOrderAlert = ref(null);
    const currentStoreId = ref(0);
    const activeStore = ref({
      name: "",
      typeLabel: "Sucursal activa",
      address: "",
      phone: "",
      rnc: "",
    });
    const canViewStockNotifications = ref(false);
    const canViewAccountsReceivableNotifications = ref(false);
    const canViewAccountsPayableNotifications = ref(false);
    const canViewExternalPurchaseNotifications = ref(false);
    const canViewBranchOrderNotifications = ref(false);
    let liveBranchOrderTimer = null;
    let knownBranchOrderIds = new Set();
    let branchOrderSnapshotInitialized = false;

    const totalNotifications = computed(
      () => totalStockNotifications.value
        + totalAccountsReceivableNotifications.value
        + totalAccountsPayableNotifications.value
        + totalExternalPurchaseNotifications.value
        + totalBranchOrderNotifications.value,
    );

    const canViewAnyNotifications = computed(
      () => canViewStockNotifications.value
        || canViewAccountsReceivableNotifications.value
        || canViewAccountsPayableNotifications.value
        || canViewExternalPurchaseNotifications.value
        || canViewBranchOrderNotifications.value,
    );

    const notificationSections = computed(() => {
      const sections = [];

      if (canViewStockNotifications.value) sections.push("stock");
      if (canViewAccountsReceivableNotifications.value) sections.push("accountsReceivable");
      if (canViewAccountsPayableNotifications.value) sections.push("accountsPayable");
      if (canViewExternalPurchaseNotifications.value) sections.push("externalPurchases");
      if (canViewBranchOrderNotifications.value) sections.push("branchOrders");

      return sections;
    });

    const activeStoreTitle = computed(() => {
      const details = [activeStore.value.name];
      if (activeStore.value.typeLabel) details.push(activeStore.value.typeLabel);
      if (activeStore.value.address) details.push(activeStore.value.address);
      if (activeStore.value.phone) details.push(`Tel: ${activeStore.value.phone}`);
      if (activeStore.value.rnc) details.push(`RNC: ${activeStore.value.rnc}`);
      return details.filter(Boolean).join(" | ");
    });

    watchEffect(() => {
      activeParent.value = active.value;
      emit("actualizar-propiedad", activeParent.value);
    });

    //estilo para hacer un scroll en x de la tabla
    //calcular el ancho del nav y hacer el scroll en x de la tabla
    const handleResize = () => {
      const bodyTable = document.querySelector(".resize-generate"); //contenedor de la tabla
      const smallMenu = document.querySelector(".grid-cols-2-small"); // cuando el nav esta en estado de 70px de ancho
      const smallMenuActive = document.querySelector(
        ".grid-cols-2-small.grid-cols-2-menu"
      ); // cuando el nav esta en estado de 200px superior
      if (bodyTable) {
        if (window.matchMedia("(max-width: 768px)").matches) {
          if (smallMenuActive) {
            bodyTable.style.width = `calc(100vw - ${70 + 42}px)`;
          } else if (smallMenu) {
            bodyTable.style.width = `calc(100vw - ${200 + 42}px)`;
          }
        } else if (window.matchMedia("(min-width: 768px)").matches) {
          if (smallMenuActive) {
            bodyTable.style.width = `calc(100vw - ${255 + 42}px)`;
          } else if (smallMenu) {
            bodyTable.style.width = `calc(100vw - ${70 + 42}px)`;
          }
        }
      }
    };

    const smallMenu = () => {
      handleResize();
      return (active.value = !active.value);
    };

    //! datos del usuario
    const loading = ref(true);
    const dataUser = ref({
      fullName: "",
      avatar: "",
      email: "",
    });

    const clearNotifications = () => {
      notifications.value = [];
      totalStockNotifications.value = 0;
      accountsReceivableNotifications.value = [];
      totalAccountsReceivableNotifications.value = 0;
      accountsPayableNotifications.value = [];
      totalAccountsPayableNotifications.value = 0;
      externalPurchaseNotifications.value = [];
      totalExternalPurchaseNotifications.value = 0;
      branchOrderNotifications.value = [];
      totalBranchOrderNotifications.value = 0;
    };

    const resolveStoreTypeLabel = (store) => {
      if (store?.es_principal || store?.principal || store?.is_main) {
        return "Sucursal principal";
      }

      return "Sucursal activa";
    };

    const loadActiveStore = async (storeId) => {
      if (!storeId) return;

      const response = await axios
        .get(`sucursales/${storeId}`)
        .catch(() => null);
      const store = response?.data || null;

      if (!store || store.msg === false) return;

      activeStore.value = {
        name: store.razon_social || store.tradeName || `Sucursal ${storeId}`,
        typeLabel: resolveStoreTypeLabel(store),
        address: store.direccion || "",
        phone: store.celular || "",
        rnc: store.rnc || "",
      };
    };

    const clearLiveBranchOrderTimer = () => {
      if (liveBranchOrderTimer) {
        window.clearTimeout(liveBranchOrderTimer);
        liveBranchOrderTimer = null;
      }
    };

    const showLiveBranchOrderAlert = (order) => {
      if (!order) return;

      clearLiveBranchOrderTimer();
      liveBranchOrderAlert.value = order;

      window.dispatchEvent(new CustomEvent("branch-order-created", {
        detail: order,
      }));

      liveBranchOrderTimer = window.setTimeout(() => {
        liveBranchOrderAlert.value = null;
        liveBranchOrderTimer = null;
      }, 4500);
    };

    const detectIncomingBranchOrders = (snapshot) => {
      if (!canViewBranchOrderNotifications.value) {
        knownBranchOrderIds = new Set();
        branchOrderSnapshotInitialized = false;
        return;
      }

      const branchOrders = Array.isArray(snapshot?.branchOrders?.data)
        ? snapshot.branchOrders.data
        : [];
      const nextIds = new Set(branchOrders.map((order) => Number(order.id)).filter(Boolean));

      if (!branchOrderSnapshotInitialized) {
        knownBranchOrderIds = nextIds;
        branchOrderSnapshotInitialized = true;
        return;
      }

      const newOrders = branchOrders.filter((order) => {
        const id = Number(order.id);
        return id > 0 && !knownBranchOrderIds.has(id);
      });

      knownBranchOrderIds = nextIds;

      if (newOrders.length > 0) {
        showLiveBranchOrderAlert(newOrders[0]);
      }
    };

    const applyNotificationSnapshot = (snapshot) => {
      if (!snapshot) {
        clearNotifications();
        return;
      }

      detectIncomingBranchOrders(snapshot);

      if (canViewStockNotifications.value) {
        notifications.value = (snapshot.stock?.data || []).map((product) => ({
          ...product,
          stock: Number(product.stock),
          stock_minimo: Number(product.stock_minimo),
        }));
        totalStockNotifications.value = Number(snapshot.stock?.total || 0);
      } else {
        notifications.value = [];
        totalStockNotifications.value = 0;
      }

      if (canViewAccountsReceivableNotifications.value) {
        accountsReceivableNotifications.value = snapshot.accountsReceivable?.data || [];
        totalAccountsReceivableNotifications.value = Number(snapshot.accountsReceivable?.total || 0);
      } else {
        accountsReceivableNotifications.value = [];
        totalAccountsReceivableNotifications.value = 0;
      }

      if (canViewAccountsPayableNotifications.value) {
        accountsPayableNotifications.value = snapshot.accountsPayable?.data || [];
        totalAccountsPayableNotifications.value = Number(snapshot.accountsPayable?.total || 0);
      } else {
        accountsPayableNotifications.value = [];
        totalAccountsPayableNotifications.value = 0;
      }

      if (canViewExternalPurchaseNotifications.value) {
        externalPurchaseNotifications.value = snapshot.externalPurchases?.data || [];
        totalExternalPurchaseNotifications.value = Number(snapshot.externalPurchases?.total || 0);
      } else {
        externalPurchaseNotifications.value = [];
        totalExternalPurchaseNotifications.value = 0;
      }

      if (canViewBranchOrderNotifications.value) {
        branchOrderNotifications.value = snapshot.branchOrders?.data || [];
        totalBranchOrderNotifications.value = Number(snapshot.branchOrders?.total || 0);
      } else {
        branchOrderNotifications.value = [];
        totalBranchOrderNotifications.value = 0;
      }
    };

    const refreshNotifications = async () => {
      if (!localStorage.getItem("access") || !hasValidSession()) {
        return;
      }

      try {
        notificationsLoading.value = true;
        currentStoreId.value = await idLocalStore();

        const response = await axios
          .get("notificaciones/resumen", {
            params: {
              idstore: currentStoreId.value,
              per_page: 8,
              sections: notificationSections.value.join(","),
            },
          })
          .catch(() => null);

        applyNotificationSnapshot(response?.data?.snapshot || null);
      } catch (error) {
        clearNotifications();
        if (showNotifications.value) {
          recoverableErrorAction("No pudimos actualizar las notificaciones.", {
            title: "Notificaciones temporalmente no disponibles.",
            notify: true,
          });
        }
      } finally {
        notificationsLoading.value = false;
      }
    };

    const toggleNotifications = async () => {
      showNotifications.value = !showNotifications.value;

      if (showNotifications.value && notifications.value.length === 0) {
        await refreshNotifications();
      }
    };

    const openProductNotification = async (notification) => {
      showNotifications.value = false;
      currentStoreId.value = await idLocalStore();

      await router.push({
        path: "/productos",
        query: {
          search: notification.codigo_de_barra,
          idstore: String(currentStoreId.value),
          stockAlert: String(Date.now()),
        },
      });
    };

    const openExternalPurchaseNotification = async (notification) => {
      showNotifications.value = false;

      await axios
        .put(`compras-externas/${notification.id}/marcar-notificacion-vista`, {
          viewed: true,
        })
        .catch(() => null);

      externalPurchaseNotifications.value = externalPurchaseNotifications.value.filter(
        (purchase) => purchase.id !== notification.id,
      );
      totalExternalPurchaseNotifications.value = Math.max(
        totalExternalPurchaseNotifications.value - 1,
        0,
      );

      await router.push({
        path: "/compras-externas",
        query: {
          externalPurchaseId: String(notification.id),
          externalNotification: String(Date.now()),
        },
      });
    };

    const openMinimumStockReport = async () => {
      showNotifications.value = false;
      currentStoreId.value = await idLocalStore();

      await router.push({
        path: "/productos-con-stock-minimo",
        query: {
          idstore: String(currentStoreId.value),
          stockAlert: String(Date.now()),
        },
      });
    };

    const openBranchOrderNotification = async (notification) => {
      showNotifications.value = false;

      await router.push({
        path: "/despacho",
        query: {
          notificationAlert: String(Date.now()),
          notificationSearch: notification.numero_cumplimiento,
          notificationFulfillmentId: String(notification.id),
        },
      });
    };

    const openAccountsReceivableNotification = async (notification) => {
      showNotifications.value = false;

      await router.push({
        path: "/cuentas-por-cobrar",
        query: {
          notificationAlert: String(Date.now()),
          notificationSearch: notification.numero_comprobante,
          notificationState: "0",
          notificationTipo: notification.tipo_alerta === "vencida" ? "vencidos" : "por_vencer",
          notificationLimiteDia: "1",
          notificationAccountId: String(notification.id),
        },
      });
    };

    const openAccountsPayableNotification = async (notification) => {
      showNotifications.value = false;

      await router.push({
        path: "/cuentas-por-pagar",
        query: {
          notificationAlert: String(Date.now()),
          notificationSearch: notification.numero_comprobante,
          notificationState: "0",
          notificationAccountId: String(notification.id),
        },
      });
    };

    const openAccountsReceivableList = async () => {
      showNotifications.value = false;

      await router.push({
        path: "/cuentas-por-cobrar",
        query: {
          notificationAlert: String(Date.now()),
          notificationState: "0",
        },
      });
    };

    const openAccountsPayableList = async () => {
      showNotifications.value = false;

      await router.push({
        path: "/cuentas-por-pagar",
        query: {
          notificationAlert: String(Date.now()),
          notificationState: "0",
        },
      });
    };

    const openExternalPurchases = async () => {
      showNotifications.value = false;

      await router.push({
        path: "/compras-externas",
        query: {
          externalInbox: String(Date.now()),
        },
      });
    };

    const openBranchOrders = async () => {
      showNotifications.value = false;

      await router.push({
        path: "/despacho",
        query: {
          notificationAlert: String(Date.now()),
        },
      });
    };

    onMounted(async () => {
      loading.value = true;
      if (!hasValidSession()) {
        clearStoredSession();
        loading.value = false;
        return;
      }

      const storedAccess = getStoredAccessData();

      if (storedAccess?.user) {
        const data = storedAccess;
        dataUser.value.fullName = data.user.name;
        dataUser.value.avatar = data.user.avatar;
        dataUser.value.email = data.user.email;
        currentStoreId.value = await idLocalStore();
        await loadActiveStore(currentStoreId.value);

        const access = await validateAccessSpecific(
          ["VER ALERTA STOCK BAJO"],
          await idLocalUser(),
          { silent: true },
        );

        canViewStockNotifications.value = Array.isArray(access)
          && access.includes("VER ALERTA STOCK BAJO");
        canViewAccountsReceivableNotifications.value = Boolean(findSubmoduleIdByDescription("/cuentas-por-cobrar"));
        canViewAccountsPayableNotifications.value = Boolean(findSubmoduleIdByDescription("/cuentas-por-pagar"));
        canViewExternalPurchaseNotifications.value = Boolean(findSubmoduleIdByDescription("/compras-externas"));
        canViewBranchOrderNotifications.value = Boolean(findSubmoduleIdByDescription("/despacho"));
      }

      loading.value = false;
    });

    onUnmounted(() => {
      clearLiveBranchOrderTimer();
    });

    const logout = async () => {
      if (getStoredAccessData()) {
        await Swal.fire({
          title: "¿Estas seguro que deseas salir del sistema?",
          text: `Si tiene trabajos pendientes y por terminar te sugerimos que termines de hacerlos...`,
          icon: "warning",
          showCancelButton: true,
          confirmButtonColor: "#315df5",
          cancelButtonColor: "#dc2626",
          confirmButtonText: "Si, ¡Salir ahora!",
          cancelButtonText: "Cancelar",
          width: "300px",
        }).then((result) => {
          if (result.isConfirmed) {
            clearStoredSession();
            window.location.href = "/login";
          }
        });
      }
    };

    const activeModalModules = () => {
      stateModalSearchModules.value = !stateModalSearchModules.value;
    };

    return {
      smallMenu,
      active,
      activeParent,
      dataUser,
      loading,
      buildApiResourceUrl,
      logout,
      activeModalModules,
      stateModalSearchModules,
      showNotifications,
      notifications,
      totalNotifications,
      totalStockNotifications,
      accountsReceivableNotifications,
      totalAccountsReceivableNotifications,
      accountsPayableNotifications,
      totalAccountsPayableNotifications,
      externalPurchaseNotifications,
      totalExternalPurchaseNotifications,
      branchOrderNotifications,
      liveBranchOrderAlert,
      totalBranchOrderNotifications,
      notificationsLoading,
      activeStore,
      activeStoreTitle,
      canViewStockNotifications,
      canViewAccountsReceivableNotifications,
      canViewAccountsPayableNotifications,
      canViewExternalPurchaseNotifications,
      canViewBranchOrderNotifications,
      canViewAnyNotifications,
      toggleNotifications,
      openProductNotification,
      openBranchOrderNotification,
      openAccountsReceivableNotification,
      openAccountsPayableNotification,
      openAccountsReceivableList,
      openAccountsPayableList,
      openExternalPurchaseNotification,
      openMinimumStockReport,
      openExternalPurchases,
      openBranchOrders,
      refreshNotifications,
      formatDateTime,
      format,
    };
  },
};
</script>

<style scoped>
.notification-bell-button {
  transform-origin: top center;
}

.notification-bell-icon {
  transform-origin: top center;
}

.notification-bell-active {
  animation: bellGlow 1.8s ease-in-out infinite;
}

.notification-bell-icon-active {
  animation: bellSwing 1.1s ease-in-out infinite;
}

.live-branch-order-alert {
  animation: liveBranchOrderAlertIn 220ms ease-out, liveBranchOrderPulse 1.2s ease-in-out 260ms 2;
}

@keyframes liveBranchOrderAlertIn {
  from {
    opacity: 0;
    transform: translateY(-10px) scale(0.98);
  }

  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes liveBranchOrderPulse {
  0%,
  100% {
    box-shadow: 0 25px 50px -12px rgba(15, 23, 42, 0.25);
  }

  50% {
    box-shadow: 0 25px 50px -8px rgba(5, 150, 105, 0.45);
  }
}

@keyframes bellSwing {
  0% {
    transform: rotate(0deg);
  }

  12% {
    transform: rotate(14deg);
  }

  24% {
    transform: rotate(-12deg);
  }

  36% {
    transform: rotate(10deg);
  }

  48% {
    transform: rotate(-8deg);
  }

  60% {
    transform: rotate(5deg);
  }

  72% {
    transform: rotate(-3deg);
  }

  100% {
    transform: rotate(0deg);
  }
}

@keyframes bellGlow {

  0%,
  100% {
    transform: translateY(0);
    filter: drop-shadow(0 0 0 rgba(255, 255, 255, 0));
  }

  50% {
    transform: translateY(-1px);
    filter: drop-shadow(0 0 8px rgba(255, 255, 255, 0.35));
  }
}

.group:hover .absolute {
  opacity: 100;
  visibility: visible;
}

.group:hover .hidden {
  visibility: visible;
}

.group:hover .invisible {
  visibility: visible;
}
</style>
